import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { dyeService } from '../services/api';
import EntityImportExportButtons from '../components/EntityImportExportButtons';
import PageBackHeader from '../components/PageBackHeader';
import NomenclatureUsageDialog, { checkNomenclatureDelete } from '../components/NomenclatureUsageDialog';

/** Текст помилок з відповіді DRF (поля + detail). */
function formatDrfErrors(data) {
  if (data == null) return null;
  if (typeof data === 'string') return data;
  if (data.detail != null) {
    return Array.isArray(data.detail) ? data.detail.join('\n') : String(data.detail);
  }
  const lines = [];
  Object.keys(data).forEach((key) => {
    const v = data[key];
    if (v == null) return;
    if (Array.isArray(v)) {
      const text = v.join(' ');
      if (key === 'name' || key === 'non_field_errors') {
        lines.push(text);
        return;
      }
      const label = key === 'article' ? 'Артикул' : key;
      lines.push(label ? `${label}: ${text}` : text);
    } else if (typeof v === 'object') {
      lines.push(`${key}: ${JSON.stringify(v)}`);
    } else if (key === 'name') {
      lines.push(String(v));
    } else {
      lines.push(`${key}: ${v}`);
    }
  });
  return lines.length ? lines.join('\n') : JSON.stringify(data);
}

function DyesPage() {
  const [dyes, setDyes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [mergeCandidate, setMergeCandidate] = useState(null);
  const [pendingPayload, setPendingPayload] = useState(null);
  const [usageDialog, setUsageDialog] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    supplier_name: '',
    article: '',
    supplier_article: '',
    color_code: '',
    description: '',
    minimum_stock: '',
    unit_price: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await dyeService.getAll();
      setDyes(response.data.results || response.data);
    } catch (error) {
      console.error('Помилка завантаження:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (dye = null) => {
    if (dye) {
      setEditing(dye.id);
      setFormData({
        name: dye.name,
        supplier_name: dye.supplier_name || '',
        article: dye.article || '',
        supplier_article: dye.supplier_article || '',
        color_code: dye.color_code || '',
        description: dye.description || '',
        minimum_stock:
          dye.minimum_stock != null && dye.minimum_stock !== '' ? String(dye.minimum_stock) : '',
        unit_price:
          dye.unit_price != null && dye.unit_price !== '' ? String(dye.unit_price) : '',
      });
    } else {
      setEditing(null);
      setFormData({
        name: '',
        supplier_name: '',
        article: '',
        supplier_article: '',
        color_code: '',
        description: '',
        minimum_stock: '',
        unit_price: '',
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditing(null);
  };

  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (saving) return;
    try {
      const upStr = String(formData.unit_price ?? '').trim().replace(',', '.');
      let unit_price = null;
      if (upStr !== '') {
        const n = parseFloat(upStr);
        if (!Number.isFinite(n) || n < 0) {
          alert('Вкажіть коректну базову ціну (невід’ємне число) або залиште поле порожнім');
          return;
        }
        unit_price = n;
      }
      const articleTrimmed = String(formData.article ?? '').trim();
      const articleNorm = articleTrimmed === '' ? null : articleTrimmed;

      const existing = editing != null ? dyes.find((d) => Number(d.id) === Number(editing)) : null;

      if (articleNorm != null) {
        const dup = dyes.find(
          (d) =>
            String(d.article ?? '').trim() === articleNorm &&
            (!editing || Number(d.id) !== Number(editing)),
        );
        if (dup) {
          alert(
            `Артикул уже використовується в іншій фарбі («${dup.name}»). Вкажіть інший артикул або залиште поле порожнім.`,
          );
          return;
        }
      }

      const payload = {
        name: formData.name,
        supplier_name: formData.supplier_name || '',
        article: articleNorm,
        supplier_article: formData.supplier_article || '',
        color_code: formData.color_code || '',
        description: formData.description || '',
        unit_price,
        minimum_stock:
          formData.minimum_stock === '' || formData.minimum_stock == null
            ? null
            : Number(formData.minimum_stock),
        ...(existing && existing.quantity != null && existing.quantity !== ''
          ? { quantity: Number(existing.quantity) }
          : {}),
      };

      const nameTrim = (formData.name || '').trim().toLowerCase();
      if (nameTrim) {
        const nameDup = dyes.find(
          (d) =>
            (d.name || '').trim().toLowerCase() === nameTrim &&
            (!editing || Number(d.id) !== Number(editing)),
        );
        if (nameDup) {
          alert('Така назва вже існує');
          return;
        }
      }
      // Перевірка на злиття (схожі назва/постачальник)
      const supNameTrim = (formData.supplier_name || '').trim().toLowerCase();
      const mergeMatch = dyes.find((d) => {
        if (editing && Number(d.id) === Number(editing)) return false;
        const dName = (d.name || '').trim().toLowerCase();
        const dSupName = (d.supplier_name || '').trim().toLowerCase();
        return (supNameTrim && dName && supNameTrim === dName) ||
               (nameTrim && dSupName && nameTrim === dSupName);
      });
      if (mergeMatch) {
        setMergeCandidate(mergeMatch);
        setPendingPayload({ payload, editing });
        return;
      }

      setSaving(true);
      if (editing) {
        await dyeService.update(editing, payload);
      } else {
        await dyeService.create(payload);
      }
      handleClose();
      loadData();
    } catch (error) {
      console.error('Помилка збереження:', error);
      const msg =
        formatDrfErrors(error.response?.data) ||
        error.response?.statusText ||
        error.message ||
        'Помилка збереження даних';
      alert(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const item = dyes.find((x) => x.id === id);
    const itemName = item?.name || '';
    try {
      const check = await checkNomenclatureDelete(dyeService, id, {
        itemName,
        confirmMessage: 'Ви впевнені, що хочете видалити цю фарбу?',
      });
      if (check.blocked) {
        setUsageDialog({ usage: check.usage, itemName: check.itemName || itemName });
        return;
      }
      if (!check.confirmed) return;
      await dyeService.delete(id);
      loadData();
    } catch (error) {
      const data = error.response?.data;
      if (error.response?.status === 409 && data?.in_use) {
        setUsageDialog({ usage: data, itemName: data.name || itemName });
        return;
      }
      console.error('Помилка видалення:', error);
      alert(formatDrfErrors(data) || 'Помилка видалення');
    }
  };

  const handleSaveSeparate = async () => {
    if (!pendingPayload) return;
    const { payload, editing: ed } = pendingPayload;
    setMergeCandidate(null);
    setPendingPayload(null);
    try {
      if (ed) { await dyeService.update(ed, payload); }
      else { await dyeService.create(payload); }
      handleClose();
      loadData();
    } catch (error) {
      const msg = formatDrfErrors(error.response?.data) || error.message || 'Помилка збереження';
      alert(msg);
    }
  };

  const handleMerge = async () => {
    if (!mergeCandidate || !pendingPayload) return;
    const { payload, editing: ed } = pendingPayload;
    setMergeCandidate(null);
    setPendingPayload(null);
    try {
      // Merged payload: поточна форма + заповнення пустих полів з кандидата
      const merged = {
        ...payload,
        name: payload.name || mergeCandidate.name || '',
        supplier_name: payload.supplier_name || mergeCandidate.supplier_name || '',
        article: payload.article || mergeCandidate.article || null,
        supplier_article: payload.supplier_article || mergeCandidate.supplier_article || '',
        color_code: payload.color_code || mergeCandidate.color_code || '',
        description: payload.description || mergeCandidate.description || '',
        unit_price: payload.unit_price ?? mergeCandidate.unit_price ?? null,
        minimum_stock: payload.minimum_stock ?? mergeCandidate.minimum_stock ?? null,
      };
      if (ed) {
        await dyeService.update(ed, merged);
        await dyeService.delete(mergeCandidate.id);
      } else {
        // Зберігаємо в картку кандидата
        await dyeService.update(mergeCandidate.id, merged);
      }
      handleClose();
      loadData();
    } catch (error) {
      const msg = formatDrfErrors(error.response?.data) || error.message || 'Помилка злиття';
      alert(msg);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PageBackHeader />
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={1}>
        <Typography variant="h4">Фарби</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <EntityImportExportButtons entityKey="dyes" label="Фарби" />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpen()}
          >
            Додати фарбу
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Назва</TableCell>
              <TableCell>Назва постачальника</TableCell>
              <TableCell>Артикул</TableCell>
              <TableCell>Артикул постачальника</TableCell>
              <TableCell>Код кольору</TableCell>
              <TableCell>Базова ціна (грн)</TableCell>
              <TableCell>Мін. залишок</TableCell>
              <TableCell>Опис</TableCell>
              <TableCell>Дії</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dyes.map((dye) => (
              <TableRow key={dye.id}>
                <TableCell>{dye.name}</TableCell>
                <TableCell>{dye.supplier_name || '—'}</TableCell>
                <TableCell>{dye.article || '—'}</TableCell>
                <TableCell>{dye.supplier_article || '—'}</TableCell>
                <TableCell>{dye.color_code || '-'}</TableCell>
                <TableCell>{dye.unit_price != null ? dye.unit_price : '—'}</TableCell>
                <TableCell>{dye.minimum_stock != null ? dye.minimum_stock : '—'}</TableCell>
                <TableCell>{dye.description || '-'}</TableCell>
                <TableCell>
                  <IconButton onClick={() => handleOpen(dye)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(dye.id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editing ? 'Редагувати фарбу' : 'Додати фарбу'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Назва"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
            />
            <TextField
              label="Назва постачальника"
              value={formData.supplier_name}
              onChange={(e) => setFormData({ ...formData, supplier_name: e.target.value })}
              fullWidth
            />
            <TextField
              label="Артикул"
              value={formData.article}
              onChange={(e) => setFormData({ ...formData, article: e.target.value })}
              fullWidth
              helperText="Унікальний серед фарб; порожньо — без артикула"
            />
            <TextField
              label="Артикул постачальника"
              value={formData.supplier_article}
              onChange={(e) => setFormData({ ...formData, supplier_article: e.target.value })}
              fullWidth
            />
            <TextField
              label="Код кольору"
              value={formData.color_code}
              onChange={(e) => setFormData({ ...formData, color_code: e.target.value })}
              fullWidth
            />
            <TextField
              label="Базова ціна (грн)"
              type="number"
              inputProps={{ min: 0, step: 'any' }}
              value={formData.unit_price}
              onChange={(e) => setFormData({ ...formData, unit_price: e.target.value })}
              fullWidth
              helperText="Та сама ціна, що в «Довіднику цін матеріалів»; порожньо — без базової ціни"
            />
            <TextField
              label="Мінімальний залишок на складі"
              type="number"
              inputProps={{ min: 0, step: 'any' }}
              value={formData.minimum_stock}
              onChange={(e) => setFormData({ ...formData, minimum_stock: e.target.value })}
              fullWidth
              helperText="Для критичних залишків на головній; порожньо — не застосовувати"
            />
            <TextField
              label="Опис"
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Скасувати</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Зберегти'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Діалог злиття */}
      <Dialog open={Boolean(mergeCandidate)} onClose={() => { setMergeCandidate(null); setPendingPayload(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Знайдено схожу позицію</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Назва або назва постачальника збігається з існуючою позицією:
          </Typography>
          <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, my: 1 }}>
            <Typography><b>Назва:</b> {mergeCandidate?.name || '—'}</Typography>
            <Typography><b>Назва постачальника:</b> {mergeCandidate?.supplier_name || '—'}</Typography>
            <Typography><b>Артикул:</b> {mergeCandidate?.article || '—'}</Typography>
            <Typography><b>Артикул постачальника:</b> {mergeCandidate?.supplier_article || '—'}</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Об'єднати в одну картку (пусті поля заповняться з іншої позиції) або зберегти як окремі?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setMergeCandidate(null); setPendingPayload(null); }}>Скасувати</Button>
          <Button onClick={handleSaveSeparate} variant="outlined">Зберегти окремо</Button>
          <Button onClick={handleMerge} variant="contained" color="primary">Об'єднати</Button>
        </DialogActions>
      </Dialog>

      <NomenclatureUsageDialog
        open={!!usageDialog}
        onClose={() => setUsageDialog(null)}
        usage={usageDialog?.usage}
        itemName={usageDialog?.itemName}
      />
    </Box>
  );
}

export default DyesPage;
